import { Inject, Injectable, isDevMode, Renderer2, RendererFactory2 } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, shareReplay, startWith } from 'rxjs/operators';
import { Breakpoint } from './breakpoint.enum';


@Injectable({
    providedIn: 'root'
})
export class BreakpointService {

    private renderer: Renderer2;
    private resizePure: BehaviorSubject<number>;

    public readonly resizePure$: Observable<number>;
    public readonly resize$: Observable<number>;
    public readonly breakpoint$: Observable<Breakpoint>;
    public readonly orientation$: Observable<OrientationType>;


    constructor(

        private rendererFactory2: RendererFactory2,
    ) {

        this.resizePure = new BehaviorSubject<number>(window.innerWidth);
        // By passing null to renderer factory, you get a hold of the default DOMrenderer,
        // and then we can listen to window from a service, which is normally not possible.
        // https://stackoverflow.com/a/55062041
        this.renderer = this.rendererFactory2.createRenderer(null, null);
        this.renderer.listen('window', 'resize', (event: CustomEvent) => this.resizePure.next(window.innerWidth));


        this.resizePure$ = this.resizePure.pipe(shareReplay(1));
        this.resize$ = this.resizePure$.pipe(debounceTime(100));
        this.breakpoint$ = this.resize$.pipe(
            map(_ => this.mapClosestBreakpoint(_)),
            startWith(Breakpoint.XSMALL),
            distinctUntilChanged(),
        );
        this.orientation$ = this.resize$.pipe(map(() => this.orientation()));
    }

    current(): Breakpoint {
        return this.mapClosestBreakpoint(this.resizePure.getValue());
    }

    orientation(): OrientationType {
        let orientation = 'portrait-primary';
        try {
            orientation = window.screen.orientation.type;
        } catch (e) {
            if (isDevMode()) {
                console.error(e);
            }
        }
        return orientation as OrientationType;
    }

    private mapClosestBreakpoint(givenScreenWidth: number): Breakpoint {
        if (givenScreenWidth < Breakpoint.SMALL) {
            return Breakpoint.XSMALL;
        } else if (givenScreenWidth < Breakpoint.MEDIUM) {
            return Breakpoint.SMALL;
        } else if (givenScreenWidth < Breakpoint.LARGE) {
            return Breakpoint.MEDIUM;
        } else if (givenScreenWidth < Breakpoint.XLARGE) {
            return Breakpoint.LARGE;
        } else if (givenScreenWidth < Breakpoint.XMLARGE) {
            return Breakpoint.XLARGE;
        } else if (givenScreenWidth < Breakpoint.XXLARGE) {
            return Breakpoint.XMLARGE;
        } else {
            return Breakpoint.XXLARGE;
        }
    }
}
